import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { blogApi, newsletterApi } from '../services/api';
import { BlogSEO } from '../components/layout/SEO';
import LoadingSpinner, { LoadingSkeleton } from '../components/common/LoadingSpinner';
import { getMediaUrl } from '../utils/environment.js';
import { FaCalendarAlt, FaClock, FaEye, FaUser, FaTag, FaShareAlt } from 'react-icons/fa';
import { FiArrowLeft } from "react-icons/fi";

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [authorImageError, setAuthorImageError] = useState(false);
  
  // Newsletter state
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({ type: '', message: '' });
  const [isSubscribing, setIsSubscribing] = useState(false);
  
  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        setImageError(false);
        setAuthorImageError(false);
        
        const articleData = await blogApi.getArticleBySlug(slug);
        
        if (!articleData) {
          throw new Error('Article not found');
        }
        
        setArticle(articleData);
        
        // Load recent articles (last 5 uploaded)
        try {
          const allArticles = await blogApi.getArticles({ ordering: '-created_at', limit: 6 });
          // Filter out current article
          const filtered = allArticles.filter(a => a.slug !== slug).slice(0, 5);
          setRecentArticles(filtered);
        } catch (recentError) {
          console.warn('Error loading recent articles:', recentError);
          setRecentArticles([]);
        }
      } catch (err) {
        console.error('Error loading article:', err);
        setError(err.message || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadArticle();
    }
  }, [slug]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubscribeStatus({ type: 'error', message: 'Please enter your email address' });
      return;
    }

    setIsSubscribing(true);
    
    try {
      await newsletterApi.subscribe(email);
      setSubscribeStatus({ 
        type: 'success', 
        message: 'Thank you for subscribing! Check your email for confirmation.' 
      });
      setEmail('');
      
      setTimeout(() => {
        setSubscribeStatus({ type: '', message: '' });
      }, 5000);
      
    } catch (error) {
      setSubscribeStatus({ 
        type: 'error', 
        message: error.message || 'Failed to subscribe. Please try again.' 
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getImageUrl = (imageSource) => {
    if (!imageSource) return '/default-blog.jpg';
    return getMediaUrl(imageSource);
  };

  const getAuthorImageUrl = (article) => {
    if (!article) return '/default-avatar.jpg';
    
    if (article.author_image_url) {
      return getMediaUrl(article.author_image_url);
    }
    
    if (article.author_image) {
      if (typeof article.author_image === 'string') {
        return getMediaUrl(article.author_image);
      }
      if (article.author_image.url) {
        return getMediaUrl(article.author_image.url);
      }
    }
    
    return '/default-avatar.jpg';
  };

  const handleImageError = () => setImageError(true);
  const handleAuthorImageError = () => setAuthorImageError(true);

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title || 'Blog Article',
        text: article?.excerpt || 'Check out this article',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-container">
          <LoadingSkeleton type="card" count={1} height="400px" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="blog-detail-page">
        <div className="container">
          <div className="error-message">
            <h2>Article not found</h2>
            <Link to="/blog" className="btn-primary">← Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryName = article.category 
    ? (typeof article.category === 'object' ? article.category.name : article.category)
    : 'Uncategorized';

  const categorySlug = article.category 
    ? (typeof article.category === 'object' ? article.category.slug : article.category.toLowerCase().replace(/\s+/g, '-'))
    : '';

  const tagsList = article.tags_list || 
                   (article.tags ? article.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []);

  return (
    <HelmetProvider>
      <div className="blog-detail-page">
        <BlogSEO article={article} />
        
        <div className="blog-detail-layout">
          {/* Main Content */}
          <main className="article-main-content">
            <article className="article-content">
              <div className="article-header">
                <div className="article-header-top">
                  {categorySlug && (
                    <Link to={`/blog?category=${categorySlug}`} className="article-category-label">
                      {categoryName}
                    </Link>
                  )}
                  
                  <div className="article-share" onClick={shareArticle}>
                    <FaShareAlt />
                    <span>Share</span>
                  </div>
                </div>
                
                <h1 className="article-title">{article.title}</h1>
                
                <div className="article-meta-row">
                  <div className="author-info">
                    <div className="author-image-wrapper">
                      <img 
                        src={authorImageError ? '/default-avatar.jpg' : getAuthorImageUrl(article)}
                        alt={article.author || 'Author'}
                        className="author-image-large"
                        onError={handleAuthorImageError}
                      />
                    </div>
                    <div className="author-details">
                      <span className="author-name">
                        <FaUser /> {article.author || 'Unknown Author'}
                      </span>
                      {article.author_role && (
                        <span className="author-role">{article.author_role}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="article-stats">
                    <span className="publish-date">
                      <FaCalendarAlt /> {formatDate(article.publish_date)}
                    </span>
                    <span className="reading-time">
                      <FaClock /> {article.read_time || '5 min read'}
                    </span>
                    {article.views && (
                      <span className="article-views">
                        <FaEye /> {article.views.toLocaleString()} views
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="featured-image-container">
                <img 
                  src={imageError ? '/default-blog.jpg' : getImageUrl(article.featured_image_url || article.featured_image)}
                  alt={article.title}
                  className="article-featured-image"
                  onError={handleImageError}
                />
              </div>
              
              <div 
                className="article-body"
                dangerouslySetInnerHTML={{ 
                  __html: article.content || '<p>No content available.</p>' 
                }}
              />
              
              {tagsList.length > 0 && (
                <div className="article-tags">
                  <h3><FaTag /> Tags</h3>
                  <div className="tags-list">
                    {tagsList.map((tag, index) => (
                      <Link 
                        to={`/blog?tag=${tag.toLowerCase().replace(/\s+/g, '-')}`} 
                        key={index} 
                        className="tag-item"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {article.author_bio && (
                <div className="author-bio-card">
                  <div className="author-bio-header">
                    <img 
                      src={authorImageError ? '/default-avatar.jpg' : getAuthorImageUrl(article)}
                      alt={article.author || 'Author'}
                      className="author-bio-image"
                      onError={handleAuthorImageError}
                    />
                    <div>
                      <h3>About the Author</h3>
                      <h4>{article.author || 'Unknown Author'}</h4>
                      {article.author_role && <p className="author-role-bio">{article.author_role}</p>}
                    </div>
                  </div>
                  <p>{article.author_bio}</p>
                </div>
              )}
              
              <div className="share-section">
                <h3>Share this article</h3>
                <div className="share-buttons">
                  <button 
                    className="share-btn twitter"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`, '_blank')}
                  >
                    <i className="fab fa-twitter"></i>
                  </button>
                  <button 
                    className="share-btn linkedin"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                  >
                    <i className="fab fa-linkedin-in"></i>
                  </button>
                  <button 
                    className="share-btn facebook"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                  >
                    <i className="fab fa-facebook-f"></i>
                  </button>
                  <button 
                    className="share-btn copy"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied!');
                    }}
                  >
                    <i className="fas fa-link"></i>
                  </button>
                </div>
              </div>
            </article>
            
            <div className="back-to-blog">
              <Link to="/blog" className="btn-secondary">
                <FiArrowLeft size={16} />
                Back to All Articles
              </Link>
            </div>
          </main>
          
          {/* Right Sidebar */}
          <aside className="article-sidebar">
             {/* <div className="sidebar-section"> */}
              <div className="newsletter-card">
                <h4>Stay Updated</h4>
                <p>Get the latest articles delivered to your inbox</p>
                <form className="newsletter-form" onSubmit={handleSubscribe}>
                  <input 
                    type="email" 
                    placeholder="Your email address" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubscribing}
                    required
                  />
                  <button type="submit" disabled={isSubscribing}>
                    {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
                {subscribeStatus.message && (
                  <div className={`subscribe-message ${subscribeStatus.type}`}>
                    {subscribeStatus.message}
                  </div>
                )}
              </div>
            {/* </div> */}
            {/* Recent Posts */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">Recent Posts</h3>
              <div className="recent-posts-list">
                {recentArticles.map((post) => (
                  <Link to={`/blog/${post.slug || post.id}`} key={post.id} className="recent-post-item">
                    <div className="recent-post-thumbnail">
                      <img 
                        src={getImageUrl(post.featured_image_url || post.featured_image)} 
                        alt={post.title || 'Blog post'} 
                        onError={(e) => e.target.src = '/default-blog.jpg'}
                      />
                    </div>
                    <div className="recent-post-content">
                      <h4>{post.title || 'Untitled'}</h4>
                      <span className="recent-post-date">
                        {formatDate(post.publish_date)}
                      </span>
                    </div>
                  </Link>
                ))}
                {recentArticles.length === 0 && (
                  <p className="no-data">No recent posts</p>
                )}
              </div>
            </div>
            
            {/* Newsletter Subscription */}
           
          </aside>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default BlogDetailPage;